import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { SpeakerHigh, UploadSimple, Spinner, PlayCircle, PauseCircle } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Compact audio preview + replacement control for a single listening exercise
 * inside the teacher's Review Queue.
 *
 * - "Play": lazy-loads audio via GET /api/exercises/{id}/audio (base64 MP3),
 *   so we don't ship all pending audio when the queue loads.
 * - "Upload replacement": POSTs a file to /api/teacher/exercises/{id}/audio
 *   which also clears any `audio_issue` flag server-side.
 */
export default function ExerciseAudioReview({ exerciseId, onReplaced }) {
  const audioRef = useRef(null);
  const fileRef = useRef(null);
  const [src, setSrc] = useState(null);       // data: URL
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [uploading, setUploading] = useState(false);

  const loadAndPlay = async () => {
    if (src) {
      const el = audioRef.current;
      if (!el) return;
      if (el.paused) { el.play(); setPlaying(true); } else { el.pause(); setPlaying(false); }
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/exercises/${exerciseId}/audio`, { withCredentials: true });
      const b64 = res.data?.audio_base64;
      if (!b64) { toast.error('Kein Audio vorhanden'); return; }
      const url = `data:audio/mp3;base64,${b64}`;
      setSrc(url);
      // Play after <audio> re-renders
      setTimeout(() => { audioRef.current?.play(); setPlaying(true); }, 0);
    } catch {
      toast.error('Audio konnte nicht geladen werden');
    } finally { setLoading(false); }
  };

  const uploadReplacement = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith('audio/')) { toast.error('Nur Audiodateien erlaubt'); return; }
    if (file.size > 15 * 1024 * 1024) { toast.error('Max 15 MB'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/teacher/exercises/${exerciseId}/audio`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Audio ersetzt');
      // Force reload on next play
      setSrc(null);
      if (audioRef.current) audioRef.current.pause();
      setPlaying(false);
      onReplaced && onReplaced();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload fehlgeschlagen');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div
      className="mt-3 rounded-lg border border-dashed border-[#D6D3D1] bg-stone-50 p-3 flex flex-wrap items-center gap-2"
      data-testid={`audio-review-${exerciseId}`}
    >
      <SpeakerHigh size={16} className="text-[#78716C]" />
      <span className="text-xs font-medium text-[#57534E]">Audio</span>

      <button
        type="button"
        onClick={loadAndPlay}
        disabled={loading}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white border border-stone-300 text-xs font-semibold text-[#292524] hover:border-[#AB1C24] hover:text-[#AB1C24] transition-colors disabled:opacity-60"
        data-testid={`audio-play-${exerciseId}`}
      >
        {loading
          ? <><Spinner size={14} className="animate-spin" /> Lädt…</>
          : playing
            ? <><PauseCircle size={16} weight="fill" /> Pause</>
            : <><PlayCircle size={16} weight="fill" /> Abspielen</>}
      </button>

      <label
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs font-semibold cursor-pointer transition-colors ${
          uploading
            ? 'bg-stone-100 border-stone-200 text-[#78716C] cursor-wait'
            : 'bg-white border-stone-300 text-[#292524] hover:border-[#2F6B50] hover:text-[#2F6B50]'
        }`}
        data-testid={`audio-replace-${exerciseId}`}
      >
        {uploading ? <Spinner size={14} className="animate-spin" /> : <UploadSimple size={14} />}
        {uploading ? 'Lädt hoch…' : 'Neu aufnehmen / ersetzen'}
        <input
          ref={fileRef}
          type="file"
          accept="audio/*"
          capture="user"
          className="hidden"
          onChange={(e) => uploadReplacement(e.target.files?.[0])}
          disabled={uploading}
        />
      </label>

      {src && (
        <audio
          ref={audioRef}
          src={src}
          onEnded={() => setPlaying(false)}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          controls
          className="w-full sm:w-64 h-8 mt-1 sm:mt-0"
          data-testid={`audio-player-${exerciseId}`}
        />
      )}
    </div>
  );
}
